import Cookies from 'js-cookie';
import { googleLogout } from '@react-oauth/google';

const baseURL = import.meta.env.VITE_API_BASE_URL;

if (!baseURL) {
  throw new Error('VITE_API_BASE_URL environment variable is not set');
}

// Simple logout function that can be called from interceptors
const performLogout = () => {
  googleLogout();
  localStorage.clear();
  // Redirect to login page
  window.location.href = '/login';
};

// Custom error class for fetch errors
class FetchError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'FetchError';
    this.status = status;
    this.data = data;
  }
}

// Type definitions matching axios structure for orval compatibility
export type RequestConfig = {
  url?: string;
  method?: string;
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

export type CancellablePromise<T> = Promise<T> & {
  cancel: () => void;
};

// Custom instance function for orval (axios-like signature, fetch implementation)
// This returns unwrapped data just like axios did
export const customInstance = <T>(
  config: RequestConfig,
  options?: RequestConfig
): CancellablePromise<T> => {
  const controller = new AbortController();

  // Merge config and options
  const mergedConfig = { ...config, ...options };
  const { url = '', method = 'GET', params, data, headers, signal } = mergedConfig;

  // Build full URL with query params
  let fullUrl = `${baseURL}${url}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle arrays by adding multiple entries with the same key
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, String(item)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      fullUrl += `?${queryString}`;
    }
  }

  // Request interceptor: Add CSRF token
  const csrfToken = Cookies.get('csrftoken');
  const fetchHeaders = new Headers(headers);
  const hasExplicitMultipart =
    fetchHeaders.get('Content-Type')?.toLowerCase().startsWith('multipart/form-data') ?? false;
  if (hasExplicitMultipart) {
    fetchHeaders.delete('Content-Type');
  }

  if (csrfToken) {
    fetchHeaders.set('X-CSRFToken', csrfToken);
  }

  const isFormData =
    typeof FormData !== 'undefined' && data instanceof FormData;

  // Build request options
  const requestInit: RequestInit = {
    method,
    headers: fetchHeaders,
    credentials: 'include',
    signal: signal || controller.signal,
  };

  if (data !== undefined && data !== null) {
    if (isFormData) {
      requestInit.body = data as FormData;
    } else if (typeof data === 'string' || data instanceof Blob || data instanceof ArrayBuffer) {
      requestInit.body = data as BodyInit;
    } else {
      if (!fetchHeaders.has('Content-Type')) {
        fetchHeaders.set('Content-Type', 'application/json');
      }
      requestInit.body = JSON.stringify(data);
    }
  }

  // Make the request and return unwrapped data (like axios)
  const promise = fetch(fullUrl, requestInit)
    .then(async (response) => {
      // Response interceptor: Handle errors
      if (!response.ok) {
        console.error(`Fetch error: ${method} ${fullUrl} - Status: ${response.status}`);

        // Parse error data first
        let errorData: unknown;
        try {
          errorData = await response.clone().json();
        } catch {
          errorData = await response.clone().text();
        }

        // Check if user doesn't have a classroom (but avoid redirect loop)
        if (errorData && typeof errorData === 'object') {
          const ed = errorData as { error_code?: string; errorCode?: string };
          // Check both error_code (snake_case) and errorCode (camelCase) for backend compatibility
          const errorCode = ed.errorCode || ed.error_code;
          if (errorCode === 'NO_USERCLASS' &&  window.location.pathname !== '/no-classroom') {
            window.location.href = '/no-classroom';
            throw new Error('No classroom assigned');
          }
        }

        // Check for 403 errors with CSRF in the message
        if (response.status === 403) {
          type ErrorData = { detail?: unknown; message?: unknown };
          let errorMessage: unknown = response.statusText;

          if (errorData && typeof errorData === 'object') {
            const ed = errorData as ErrorData;
            errorMessage = ed.detail ?? ed.message ?? response.statusText;
          }

          // Check if error message mentions CSRF
          if (
            typeof errorMessage === 'string' &&
            errorMessage.toLowerCase().includes('csrf')
          ) {
            console.warn('CSRF token validation failed. Logging out...');
            performLogout();
            throw new Error('Session expired. Please log in again.');
          }

          throw new FetchError(
            typeof errorMessage === 'string'
              ? errorMessage
              : response.statusText,
            response.status,
            errorData
          );
        }

        throw new FetchError(response.statusText, response.status, errorData);
      }

      // Parse and return data directly (unwrapped, like axios)
      const contentType = response.headers.get('content-type');
      const rawText = await response.text();

      if (contentType && contentType.includes('application/json')) {
        if (!rawText) {
          return undefined;
        }

        try {
          return JSON.parse(rawText);
        } catch (error) {
          console.warn('Failed to parse JSON response, returning raw text.', {
            url,
            error,
            rawText,
          });
          return rawText as T;
        }
      }

      return rawText as T;
    })
    .catch((error) => {
      throw error;
    }) as CancellablePromise<T>;

  // Add cancel method
  promise.cancel = () => {
    controller.abort();
  };

  return promise;
};

export default customInstance;
